import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  CircleDollarSign,
  Filter,
  Info,
  LockKeyhole,
  Network,
  RefreshCw,
  Route,
  ShieldCheck,
  Sparkles,
  Trophy,
  Unlock,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MemberDataState } from '../components/DataState';
import { OnChainMatrixSection } from '../components/OnChainMatrixCard';
import { PageHeading, ProgressBar, Segmented, StatusPill } from '../components/Ui';
import type { SlotSummaryView } from '../data/view-model';
import { formatToken } from '../lib/format';
import { highestSlot, isSubSlot, previousSlot, slotLabel, slotRank } from '../lib/slots';
import { useAppData } from '../state/AppDataContext';

type SlotFilter = 'all' | 'active' | 'locked';

function SlotCard({ slot, index }: { slot: SlotSummaryView; index: number }) {
  // Rank, not the raw key: Slot ½ (50) / ¼ (25) sit BELOW Slot 1 and are never "advanced".
  const advanced = slotRank(slot.number) > 12;
  const previous = previousSlot(slot.number);
  return (
    <motion.article
      layout
      className={`slot-card panel ${slot.active ? 'unlocked' : 'locked'} ${advanced ? 'advanced-tier' : ''}`}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.025, 0.2) }}
      whileHover={slot.active ? { y: -5 } : { y: -2 }}
    >
      <div className="slot-top">
        <div className="slot-number"><span>{isSubSlot(slot.number) ? slotLabel(slot.number) : slot.number.toString().padStart(2, '0')}</span><i /></div>
        <div><small>{advanced ? 'ADVANCED TIER' : isSubSlot(slot.number) ? 'ERA SUB-SLOT' : 'MATRIX LEVEL'}</small><h3>{slot.label}{slot.virtual ? <span className="slot-virtual-tag">virtual</span> : null}</h3></div>
        {slot.active ? <StatusPill tone="green"><Unlock size={11} /> {slot.virtual ? 'Virtual · on-chain' : 'Active on-chain'}</StatusPill> : <span className="lock-orb"><LockKeyhole size={16} /></span>}
      </div>

      <div className="slot-price"><span className="coin-dot">₿</span><strong>{formatToken(slot.price).replace(' BTCB', '')}</strong><em>BTCB</em></div>

      {slot.active ? (
        <>
          <div className="slot-cycle"><span>Current cycle</span><strong>{slot.cycle} <small>on-chain</small></strong><ProgressBar value={slot.progress} /></div>
          <div className="slot-metrics">
            <div title="Seats filled in the running cycle (on-chain)"><Network /><strong>{slot.placements}</strong><span>Positions</span></div>
            <div title="Slot-owner seats filled on-chain, every cycle"><CircleDollarSign /><strong>{slot.received}</strong><span>Owner seats</span></div>
            <div title="Cycles closed on-chain (recycle seats)"><RefreshCw /><strong>{slot.recycled}</strong><span>Recycles</span></div>
          </div>
          <div className="slot-earning" title="Sum of the SeatFilled amounts of this slot's owner seats"><span>Owner seats value</span><strong>{formatToken(slot.earning)}</strong></div>
          <Link className="slot-link" to={`/slots/${slot.number}`} aria-label={`Explore ${slot.label}`}>Explore matrix <ArrowRight size={16} /></Link>
        </>
      ) : (
        <div className="locked-content">
          <div className="locked-lines"><i /><i /><i /></div>
          <div className="unlock-condition"><ShieldCheck /><div><strong>Progression locked</strong><span>{previous === null ? 'Not opened yet' : `Complete Slot ${slotLabel(previous)} eligibility to unlock`}</span></div></div>
          <button type="button" disabled>Not yet eligible <LockKeyhole size={14} /></button>
        </div>
      )}
      {advanced ? <div className="advanced-ribbon"><Sparkles size={12} /> Advanced</div> : null}
    </motion.article>
  );
}

export function SlotsPage() {
  const data = useAppData();
  const [filter, setFilter] = useState<SlotFilter>('all');
  const visible = useMemo(() => data.slots.filter((slot) => filter === 'all' || (filter === 'active' ? slot.active : !slot.active)), [data.slots, filter]);
  const activeCount = data.slots.filter((slot) => slot.active).length;
  // Highest active slot by progression rank (Slot ½ = 50 is below Slot 1); a virtual sub-slot is never the journey's head.
  const currentSlot = highestSlot(data.slots.filter((slot) => slot.active && !slot.virtual).map((slot) => slot.number));
  const subSlotCount = data.slots.filter((slot) => isSubSlot(slot.number)).length;
  if (!data.member) return <MemberDataState mode={data.mode} error={data.error} onRetry={data.refresh} resource="slot data" />;
  return (
    <>
      <PageHeading
        eyebrow="POSITION ENGINE"
        title="Your slot journey"
        description="Sixteen progressively unlocked matrices, with every placement and route independently verifiable."
        actions={<StatusPill tone="violet"><Route size={13} /> ID {data.member.id}</StatusPill>}
      />
      <section className="slot-overview panel">
        <div className="slot-overview-icon"><Trophy /></div>
        <div><span className="eyebrow">JOURNEY PROGRESS</span><h2>{activeCount} of {data.slots.length} slots active</h2><p>A slot is active only when the contract has activated it; every seat shown is an indexed on-chain event.</p></div>
        <div className={`journey-track${subSlotCount ? ` sub-${subSlotCount}` : ''}`} aria-label={`${activeCount} of ${data.slots.length} slots active`}>
          {data.slots.map((slot) => <span key={slot.number} className={`${slot.active ? 'active' : ''}${slot.virtual ? ' virtual' : ''}`} title={`${slot.label}${slot.virtual ? ' · virtual' : ''}: ${slot.active ? 'activated on-chain' : 'not activated on-chain'}`}>{slotLabel(slot.number)}</span>)}
        </div>
        {currentSlot > 0 ? <Link to={`/slots/${currentSlot}`} className="button secondary">Continue journey <ArrowRight size={15} /></Link> : null}
      </section>

      <div className="toolbar">
        <Segmented<SlotFilter> value={filter} onChange={setFilter} label="Filter slots" options={[{ value: 'all', label: `All ${data.slots.length}` }, { value: 'active', label: 'Active' }, { value: 'locked', label: 'Locked' }]} />
        <span className="toolbar-note"><Filter size={14} /> Prices = the contract&apos;s slot values</span>
      </div>

      <motion.section layout className="slots-grid">
        <AnimatePresence mode="popLayout">
          {visible.map((slot, index) => <SlotCard key={slot.number} slot={slot} index={index} />)}
        </AnimatePresence>
      </motion.section>

      {/* The ID's full on-chain matrix record (seats, routing, rewards, vault sub-ledger) - moved here from the Leaders Club page. */}
      <OnChainMatrixSection bdId={data.member.id} />

      <div className="spec-callout"><Info /><div><strong>Read from the chain</strong><p>Slot prices are the contract&apos;s slot values; positions, owner seats and recycles are the matrix contract&apos;s SeatFilled events. What was actually paid to you is in the income card on the overview (vault RewardPaid / Claimed events).</p></div></div>
    </>
  );
}
